/** @jsxImportSource preact */
import { useAuth } from '../providers/auth'
import { Dashboard } from './Dashboard'
import { useState, useEffect } from 'preact/hooks'
import { config } from '../config'

export function Home() {
  const { user, checkAuth } = useAuth()
  const [isSigningIn, setIsSigningIn] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)

  // Listen for message from the popup window
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      // Validate message type
      if (event.data && event.data.type === 'AUTH_CODE') {
        const { code } = event.data
        setAuthError(null)

        try {
          // Use the code to authenticate with the server
          const response = await fetch(`${config.routePrefix}/authenticate`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ code }),
          })

          if (!response.ok) {
            throw new Error(`Authentication failed with status: ${response.status}`)
          }
          // After authentication, check auth status
          await checkAuth()
        } catch (err) {
          console.error('Authentication error:', err)
          setAuthError('Authentication failed. Please try again.')
        } finally {
          setIsSigningIn(false)
        }
      }
    }

    // Add event listener for messages from popup
    window.addEventListener('message', handleMessage)

    // Clean up event listener on unmount
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [checkAuth])

  if (user) {
    return <Dashboard />
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
      <div className="p-8 bg-white rounded-lg shadow-md">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">Welcome to PLT Ops App</h1>
        {authError && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{authError}</div>}
        <button
          onClick={() => {
            setIsSigningIn(true)
            setAuthError(null)

            // Get the current window origin for postMessage security
            const currentOrigin = window.location.origin

            // Construct the login URL with origin parameter
            const loginUrl = `${config.routePrefix}/login?origin=${encodeURIComponent(currentOrigin)}`

            const popup = window.open(
              loginUrl,
              'PLT Ops Login',
              [
                'popup=yes', // Force popup mode
                'width=800', // Set width
                'height=600', // Set height
                'menubar=no', // Hide menu bar
                'toolbar=no', // Hide toolbar
                'location=no', // Hide address bar
                'status=no', // Hide status bar
                'resizable=yes', // Allow resizing
                'scrollbars=yes', // Allow scrolling
              ].join(','),
            )

            if (!popup) {
              setAuthError('Pop-up was blocked. Please allow pop-ups for this site.')
              setIsSigningIn(false)
              return
            }

            // Monitor popup status - only to detect if user closes it without completing auth
            const checkPopup = setInterval(() => {
              if (popup && popup.closed && isSigningIn) {
                clearInterval(checkPopup)
                setIsSigningIn(false)
              }
            }, 1000)
          }}
          disabled={isSigningIn}
          className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-400"
        >
          {isSigningIn ? 'Signing in...' : 'Sign in'}
        </button>
      </div>
    </div>
  )
}
